/**
 * Utility functions for API operations
 */

/**
 * Sanitizes payload by converting empty strings to undefined
 * This prevents backend validation errors that reject empty strings
 */
export const sanitizePayload = <T extends Record<string, unknown>>(payload: T): T => {
  const sanitized = {...payload}

  for (const key in sanitized) {
    if (Object.prototype.hasOwnProperty.call(sanitized, key)) {
      const value = sanitized[key]

      if (value === '' || (typeof value === 'string' && value.trim() === '')) {
        // Convert empty strings to undefined
        ;(sanitized as Record<string, unknown>)[key] = undefined
      } else if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value.constructor === Object &&
        !(value instanceof Date)
      ) {
        // Recursively sanitize nested objects
        ;(sanitized as Record<string, unknown>)[key] = sanitizePayload(
          value as Record<string, unknown>,
        )
      }
    }
  }

  return sanitized
}

/**
 * API Error Handling Pattern:
 *
 * API services should just throw the original error:
 *   throw error
 *
 * Components should catch and handle with errorFunction from utils/errorFunction.ts:
 *
 * Example usage in components:
 * ```tsx
 * import { errorFunction } from '../utils/errorFunction'
 * import { useSnackbar } from '../hooks/useSnackbar'
 *
 * const { isSnackbarOpen, setIsSnackbarOpen, customMessage, setCustomMessage } = useSnackbar()
 *
 * try {
 *   await companyApi.createCompany(data)
 * } catch (error) {
 *   errorFunction(error, setIsSnackbarOpen, setCustomMessage)
 * }
 *
 * return (
 *   <div>
 *     {/* Your component JSX *\/}
 *     {/* ReusableSnackbar component with props:
 *        isOpen={isSnackbarOpen}
 *        onClose={() => setIsSnackbarOpen(false)}
 *        message={customMessage}
 *        type="error"
 *     *\/}
 *   </div>
 * )
 * ```
 */
